// Core modules
import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { FormGroup, UntypedFormBuilder, FormControl } from '@angular/forms';
import { ClientService } from '@services/client.service';
import {MatTableDataSource} from '@angular/material/table';
import * as _ from 'lodash';
import {MatDialog} from '@angular/material/dialog';
import {ConfigService} from '@services/config.service';
import {ATSService} from '@services/ats.service';
import {JsonContentDialogComponent, JsonContentDialogComponentData} from '@components/survale-widgets/widgets-common/json-content-dialog/json-content-dialog.component';
import * as moment from 'moment';
import {ToastrService} from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

/**
 * Client Index Weights Component
 */
@Component({
    selector: 'app-other-client-settings',
    templateUrl: './other-client-settings.component.html',
    styleUrls: ['./other-client-settings.component.scss'],
    standalone: false
})
export class OtherClientSettingsComponent implements OnInit {
  @Input() settings: any;
  public otherSettings: any;
  private defaultLogLimit = 5;
  constructor(private fb: UntypedFormBuilder,
    private clientService: ClientService,
    public dialog: MatDialog,
    private atsService: ATSService,
    private toastr: ToastrService
  ) {

  }

  async ngOnInit(): Promise<void> {
    this.otherSettings = (await firstValueFrom<any>(this.clientService.getOtherClientSettings())).body;
    for (const setting of this.otherSettings.scheduledJobs) {
      try {
        setting.jobSettings = JSON.parse(setting.jobSettings);
        setting.displayedColumnsJobSettings = ['name', 'email'];
        setting.dataSourceJobSettings = new MatTableDataSource(setting.jobSettings.reportToEmails);
      } catch (e) {}
    }
    for (const job of this.otherSettings.sqs_Jobs) {
      job.inProgress = false;
      job.atsLogsDataSource = this.atsLogsToDS(job);
      job.displayedLogsColumns = ['requestedAt', 'status', 'found', 'processed', 'params', 'logURL'];
      job.disableRefresh = false;
      if (job.clientCredentialsUpdateable) {
        job.newUsername = '';
        job.newPassword = '';
        job.credentialsValid = false;
        job.credentialCheckStatus = '';
      }
      if (!job.isDeactivated) {
        job.newCandidatesFound = false;
        job.newCandidatesFoundCount = 0;
      }
      // if clientCredentialsUpdateable is true, then we can check and update the client credentials
      try {
        if (typeof job.settings === 'string') {
          job.settings = JSON.parse(job.settings);
        }
        const fieldMapTable = this.getFieldMapTableData(job);
        job.displayedColumns = ['clientFieldName', 'internalFieldName', 'displayFieldName',
          'fieldType', 'alias'];
        job.dataSource = new MatTableDataSource(fieldMapTable.data);
        // }
      } catch (e) {}
      try {
        job.emailListSettings = JSON.parse(job.emailListSettings);
      } catch (e) {}
      job.humanizedInterval = moment.duration(job.settings.jobInterval, 'minutes').humanize();
    }
    this.otherSettings.sqs_Jobs = _.sortBy(this.otherSettings.sqs_Jobs, ['isDeactivated']);
    // console.log('this.otherSettings', this.otherSettings);
  }


  showJSONContentModal(dialog: MatDialog, jsonContent: any): void {
    const config = ConfigService.getDialogConfigWide();
    const data: JsonContentDialogComponentData = {jsonContent};
    config.data = data;
    dialog.open(JsonContentDialogComponent, config);
  }

  atsLogsToDS(job: any): MatTableDataSource<any> {
    const atsLogsDS = [];
    for (const log of job.atsLogs) {
      let params = _.pick(log.options, ['url', 'method', 'qs', 'body', 'params']);
      if (!params.url) {
        params = log.options;
      }
      const logDS = {
        requestedAt: log.requestedAt,
        found: '',
        processed: '',
        skipped: '',
        status: 'success',
        requestParams: params,
        logURL: log.url,
        errorResponse: log.errorResponse
      };
      if (log.dataLog) {
        logDS.found = log.dataLog.candidatesFound || 0;
        logDS.processed = log.dataLog.candidatesProcessed || 0;
        logDS.skipped = (log.dataLog.candidatesFiltered || 0) + (log.dataLog.candidatesWOEmailAddress || 0) +
          (log.dataLog.candidatesWOStatus || 0);
      }
      if (!job.inProgress && (job.batchJobsAvl === 'y' || (!log.responseCode && !log.responseAt && !log.errorResponseAt))) {
        job.inProgress = true;
        logDS.status = 'in progress...';
      } else if (log.responseCode) {
        logDS.status = log.responseCode === 200 ? 'success' : `error (${log.responseCode})`;
      }
      atsLogsDS.push(logDS);
    }
    return new MatTableDataSource(atsLogsDS);
  }

  async reloadSqsJobs(job: any): Promise<void> {
    job.disableRefresh = true;
    const refreshedJob = (await firstValueFrom<any>(this.clientService.getOtherClientSettings(job.id))).body;
    _.assign(job, refreshedJob.sqs_Jobs[0]);
    job.inProgress = false;
    job.atsLogsDataSource = this.atsLogsToDS(job);
    job.disableRefresh = false;
  }

  async scheduleImport(job: any): Promise<void> {
    try {
      if (job.newCandidatesFound) {
        await firstValueFrom<any>(this.atsService.scheduleATSJob(job.id, job.settings.jobInterval));
        this.toastr.success('Import scheduled successfully. Refreshing status in 60 seconds.');
        job.newCandidatesFound = false;
        setTimeout(async (): Promise<void> => {
          await this.reloadSqsJobs(job);
        }, 60000); // Reload job status after 60 seconds
      }
    } catch (error) {
      console.error('Error scheduling import:', error);
      this.toastr.error('Error scheduling import.');
    }
  }

  async testAPI(job: any): Promise<void> {
    job.checkingInProgress = true;
    job.newCandidatesFound = false;
    try {
      const resp = (await firstValueFrom<any>(this.atsService.testATSJob(job.id))).body;
      if (resp.dataAvailable) {
        if (job.inProgress) {
          this.toastr.warning('Job is already in progress. Please wait for it to complete before testing again.');
          return;
        }
        job.newCandidatesFound = true;
        this.toastr.success('Candidates found, will be imported in the next schedule.');
      } else {
        this.toastr.info('No candidates found to process.');
      }
      console.log(resp);
    } catch (error) {
      console.error('Error testing API:', error);
      this.toastr.error('Error testing API.');
      this.showJSONContentModal(this.dialog, error.error || error.message || error);
    }
    job.checkingInProgress = false;
  }

  async checkClientCredentials(job: any): Promise<void> {
    if (!job.newUsername || !job.newPassword) {
      job.credentialCheckStatus = 'Username and Password are required.';
      job.credentialsValid = false;
      return;
    }
    // Reset status before check to provide user feedback
    job.credentialCheckStatus = 'Validating...';
    job.credentialsValid = false;

    try {
      const payload = {
        sqsJobId: job.id,
        apiUrl: job.apiUrl,
        
        connectionSettings: {
          userName: job.newUsername,
          passWord: job.newPassword
        }
      };
      // Assuming the service needs the job ID to identify which configuration to test against.
      await firstValueFrom<any>(this.atsService.checkClientCredentials(payload));
      job.credentialCheckStatus = 'Success! Credentials are valid and can be updated.';
      job.credentialsValid = true;
    } catch (error) {
      job.credentialCheckStatus = 'Validation failed. Please check the credentials and try again.';
      job.credentialsValid = false;
      console.error('Error checking client credentials:', error);
    }
  }

  async updateClientCredentials(job: any): Promise<void> {
    if (!job.credentialsValid) {
      job.credentialCheckStatus = 'Please validate credentials before updating.';
      return;
    }
    try {
      const payload = {
        sqsJobId: job.id,
        apiUrl: job.apiUrl,
        connectionSettings: {
          userName: job.newUsername,
          passWord: job.newPassword
        }
      };
      await firstValueFrom<any>(this.atsService.updateClientCredentials(payload));
      job.credentialCheckStatus = 'Credentials updated successfully!';
      // Reset form state on success
      job.newUsername = '';
      job.newPassword = '';
      job.credentialsValid = false;
      setTimeout((): void => { job.credentialCheckStatus = ''; }, 5000); // Clear message after 5s
    } catch (error) {
      job.credentialCheckStatus = 'An error occurred while updating credentials.';
      console.error('Error updating client credentials:', error);
    }
  }

  getFieldMapTableData(job) {
    const fieldMap = job?.settings?.api?.fieldMap;
    if (_.isNil(fieldMap)) {
      return {data: []};
    }

    const customFiledMapping = JSON.parse(job?.emailListSettings).emailListJSON?.customFieldMapping;
    const rows = [];

    _.keys(fieldMap).forEach((key) => {
      const value = fieldMap[key];

      if (Array.isArray(value) && key === 'customFields') {
        value.forEach((item) => {
          const displayName = _.findKey(customFiledMapping, (val) => val === item.fieldName);
          // fetch the client field name
          const fieldMapping = job.fieldMappings.find((r) => r.innerField === item.fieldName);
          _.each(fieldMapping.clientFields, cf => {
            const row = {
              internalFieldName: item.fieldName,
              clientFieldName: cf.fieldName,
              displayFieldName: displayName,
              calculatedFieldName: '',
              fieldType: item.fieldType ? item.fieldType : '',
              defaultValue: item.defaultValue ? item.defaultValue : '',
              dateFormat: item.dateFormat ? item.dateFormat : '',
              alias: _.isNil(this.settings.operationalFieldAliases[item.fieldName]) ? '-' :
                this.settings.operationalFieldAliases[item.fieldName]
            };
            if (item.type && item.type === 'regex-test') {
              row.internalFieldName = '-';
              row.calculatedFieldName = item.fieldName;
            }
            rows.push(row);
          });          
        });
      } else if (typeof value === 'object' && value !== null) {
        _.keys(value).forEach((subKey) => {
          rows.push({
            internalFieldName: `${key}.${subKey}`,
            clientFieldName: value[subKey],
            displayName: '-',
          });
        });
      } else {
        rows.push({
          internalFieldName: key,
          clientFieldName: value,
          displayName: '-',
        });
      }
    });

    return {data: rows};
  }
}
